import { Injectable } from '@angular/core';
import { chunk } from 'lodash-es';
import {
	BehaviorSubject,
	catchError,
	concatMap,
	delay,
	from,
	map,
	Observable,
	of,
	reduce,
	ReplaySubject,
	retry,
	Subject,
	Subscription,
	take,
	takeUntil,
	timeout
} from 'rxjs';

import {
	ApolloCacheManager,
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { Group, GroupOperationError } from '@shure/cloud/shared/models/devices';
import { monitorLoginState, OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { RequestResponse, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AddDevicesToGroupApiInput,
	CreateGroupApiInput,
	DeleteGroupApiInput,
	GroupMemberOperationResponse,
	GroupsApiService,
	MoveGroupApiInput,
	UngroupDevicesApiInput,
	RenameGroupApiInput
} from '../api/groups-api.service';

import {
	CreateGroupGQL,
	DeleteGroupGQL,
	GroupFragment,
	GroupsQueryGQL,
	GroupsSubscriptionGQL,
	GroupSubscriptionGQL,
	RenameGroupGQL,
	AddDevicesToGroupGQL,
	MoveGroupGQL,
	UngroupDevicesGQL
} from './graphql/generated/cloud-sys-api';
import { mapGroupFromSysApi } from './mappers/map-group';
import { mapGroupOperationErrorFromSysApi } from './mappers/map-group-operation-error';

const QUERY_PAGE_SIZE = 1000;

export const MUTATION_BATCH_SIZE = 50;
const MUTATION_BATCH_DELAY = 1000;
const MUTATION_TIMEOUT = 15000;

function isRetryableGroupOperationError(error: GroupOperationError | null | undefined): boolean {
	return !!error && [GroupOperationError.Unknown].includes(error);
}

@Injectable()
export class SysApiGroupsApiService implements GroupsApiService {
	public readonly groupsLoadingComplete$ = new BehaviorSubject<boolean>(false);
	public readonly groupAdded$ = new Subject<Group>();
	public readonly groupRemoved$ = new Subject<string>();
	public readonly groupSnapshot$ = new Subject<Group>();
	public readonly suspended$ = new Subject<void>();

	private readonly groupIds = new Set<string>();
	private destroy$ = new Subject<void>();
	private isInitialized = false;
	private readonly logger: ILogger;
	private readonly subscriptionReady$ = new ReplaySubject<void>(1);

	private readonly groupSubscriptionManager = new SubscriptionManager({
		subscriptionType: 'groups',
		create: (config): Subscription => this.createGroupSubscription(config),
		retryWaitMs: 10_000,
		maxRetryAttempts: 3
	});

	constructor(
		logger: ILogger,
		oktaService: OktaInterfaceService,
		private readonly groupsQueryGQL: GroupsQueryGQL,
		private readonly groupsSubscriptionGQL: GroupsSubscriptionGQL,
		private readonly groupSubscriptionGQL: GroupSubscriptionGQL,
		private readonly createGroupGQL: CreateGroupGQL,
		private readonly deleteGroupGQL: DeleteGroupGQL,
		private readonly renameGroupGQL: RenameGroupGQL,
		private readonly moveGroupGQL: MoveGroupGQL,
		private readonly addDevicesToGroupGQL: AddDevicesToGroupGQL,
		private readonly ungroupDevicesGQL: UngroupDevicesGQL,
		private readonly apolloCacheManager: ApolloCacheManager
	) {
		this.logger = logger.createScopedLogger('SysApiGroupsApiService');

		monitorLoginState(oktaService, {
			onLogIn: this.initService.bind(this),
			onLogOut: this.suspendService.bind(this)
		});
	}

	public getCachedGroups$(): Observable<Group[]> {
		const apolloCache = this.apolloCacheManager.getApolloCache('sysApi');
		if (!apolloCache) {
			this.logger.error('getCachedGroups', 'Apollo cache "sysApi" not found');
			return of([]);
		}

		const cachedGroups: Group[] = [];
		for (const id of this.groupIds) {
			const cachedGroup = apolloCache.readFragment<GroupFragment>({
				// eslint-disable-next-line @typescript-eslint/naming-convention
				id: apolloCache.identify({ __typename: 'Group', id }),
				fragment: GroupFragment
			});

			const group = mapGroupFromSysApi(cachedGroup);
			if (group) {
				cachedGroups.push(group);
			}
		}

		return of(cachedGroups);
	}

	public createGroup(input: CreateGroupApiInput): Observable<RequestResponse<GroupOperationError>> {
		return this.createGroupGQL.mutate({ variables: { input } }).pipe(
			map((result) => {
				// If we encountered a retryable error, throw so that the retry operator can handle it
				if (isRetryableGroupOperationError(result.data?.createGroup.error)) {
					throw result.data?.createGroup.error;
				}

				return {
					state: result.data?.createGroup.error ? ResponseState.Error : ResponseState.Done,
					error: mapGroupOperationErrorFromSysApi(result.data?.createGroup.error)
				};
			}),
			retry({ count: 2, delay: 1000 }),
			catchError((error) =>
				of({
					state: ResponseState.Error,
					error: mapGroupOperationErrorFromSysApi(error)
				})
			)
		);
	}

	public deleteGroup(input: DeleteGroupApiInput): Observable<RequestResponse<GroupOperationError>> {
		return this.deleteGroupGQL.mutate({ variables: { input } }).pipe(
			map((result) => {
				// If we encountered a retryable error, throw so that the retry operator can handle it
				if (isRetryableGroupOperationError(result.data?.deleteGroup.error)) {
					throw result.data?.deleteGroup.error;
				}

				return {
					state: result.data?.deleteGroup.error ? ResponseState.Error : ResponseState.Done,
					error: mapGroupOperationErrorFromSysApi(result.data?.deleteGroup.error)
				};
			}),
			retry({ count: 2, delay: 1000 }),
			catchError((error) =>
				of({
					state: ResponseState.Error,
					error: mapGroupOperationErrorFromSysApi(error)
				})
			)
		);
	}

	public renameGroup(input: RenameGroupApiInput): Observable<RequestResponse<GroupOperationError>> {
		return this.renameGroupGQL.mutate({ variables: { input } }).pipe(
			map((result) => {
				// If we encountered a retryable error, throw so that the retry operator can handle it
				if (isRetryableGroupOperationError(result.data?.renameGroup.error)) {
					throw result.data?.renameGroup.error;
				}

				return {
					state: result.data?.renameGroup.error ? ResponseState.Error : ResponseState.Done,
					error: mapGroupOperationErrorFromSysApi(result.data?.renameGroup.error)
				};
			}),
			retry({ count: 2, delay: 1000 }),
			catchError((error) =>
				of({
					state: ResponseState.Error,
					error: mapGroupOperationErrorFromSysApi(error)
				})
			)
		);
	}

	public moveGroup(input: MoveGroupApiInput): Observable<RequestResponse<GroupOperationError>> {
		return this.moveGroupGQL.mutate({ variables: { input } }).pipe(
			map((result) => {
				// If we encountered a retryable error, throw so that the retry operator can handle it
				if (isRetryableGroupOperationError(result.data?.moveGroup.error)) {
					throw result.data?.moveGroup.error;
				}

				return {
					state: result.data?.moveGroup.error ? ResponseState.Error : ResponseState.Done,
					error: mapGroupOperationErrorFromSysApi(result.data?.moveGroup.error)
				};
			}),
			retry({ count: 2, delay: 1000 }),
			catchError((error) =>
				of({
					state: ResponseState.Error,
					error: mapGroupOperationErrorFromSysApi(error)
				})
			)
		);
	}

	public addDevicesToGroup(input: AddDevicesToGroupApiInput): Observable<GroupMemberOperationResponse> {
		// Due to DynamoDB write throttling constraints, devices must be processed in batches of 50 with no more than 1 batch sent per second.
		return from(chunk(input.devices, MUTATION_BATCH_SIZE)).pipe(
			concatMap((devices) =>
				this.addDevicesToGroupGQL.mutate({ variables: { input: { group: input.group, devices } } }).pipe(
					map((result) => {
						// If we encountered a retryable error, throw so that the retry operator can handle it
						if (isRetryableGroupOperationError(result.data?.addDevicesToGroup.error)) {
							throw result.data?.addDevicesToGroup.error;
						}

						return {
							state: result.data?.addDevicesToGroup.error ? ResponseState.Error : ResponseState.Done,
							error: mapGroupOperationErrorFromSysApi(result.data?.addDevicesToGroup.error),
							results: (result.data?.addDevicesToGroup.results ?? []).map((deviceResult) => ({
								id: deviceResult.id,
								status: deviceResult.error ? ResponseState.Error : ResponseState.Done,
								error: mapGroupOperationErrorFromSysApi(deviceResult.error)
							}))
						};
					}),
					retry({ count: 2, delay: 1000 }),
					catchError((error) =>
						of({
							state: ResponseState.Error,
							error: mapGroupOperationErrorFromSysApi(error),
							results: []
						})
					),
					timeout(MUTATION_TIMEOUT),
					delay(MUTATION_BATCH_DELAY)
				)
			),
			reduce(
				(completeResponse, currentBatchResponse) => {
					if (currentBatchResponse.state === ResponseState.Error) {
						completeResponse.state = ResponseState.Error;
					}

					completeResponse.results.push(...currentBatchResponse.results);
					return completeResponse;
				},
				{
					state: ResponseState.Done,
					results: []
				} as GroupMemberOperationResponse
			)
		);
	}

	public ungroupDevices(input: UngroupDevicesApiInput): Observable<GroupMemberOperationResponse> {
		// Due to DynamoDB write throttling constraints, devices must be processed in batches of 50 with no more than 1 batch sent per second.
		return from(chunk(input.devices, MUTATION_BATCH_SIZE)).pipe(
			concatMap((devices) =>
				this.ungroupDevicesGQL.mutate({ variables: { input: { devices } } }).pipe(
					map((result) => {
						// If we encountered a retryable error, throw so that the retry operator can handle it
						if (isRetryableGroupOperationError(result.data?.ungroupDevices.error)) {
							throw result.data?.ungroupDevices.error;
						}

						return {
							state: result.data?.ungroupDevices.error ? ResponseState.Error : ResponseState.Done,
							error: mapGroupOperationErrorFromSysApi(result.data?.ungroupDevices.error),
							results: (result.data?.ungroupDevices.results ?? []).map((deviceResult) => ({
								id: deviceResult.id,
								status: deviceResult.error ? ResponseState.Error : ResponseState.Done,
								error: mapGroupOperationErrorFromSysApi(deviceResult.error)
							}))
						};
					}),
					retry({ count: 2, delay: 1000 }),
					catchError((error) =>
						of({
							state: ResponseState.Error,
							error: mapGroupOperationErrorFromSysApi(error),
							results: []
						})
					),
					timeout(MUTATION_TIMEOUT),
					delay(MUTATION_BATCH_DELAY)
				)
			),
			reduce(
				(completeResponse, currentBatchResponse) => {
					if (currentBatchResponse.state === ResponseState.Error) {
						completeResponse.state = ResponseState.Error;
					}

					completeResponse.results.push(...currentBatchResponse.results);
					return completeResponse;
				},
				{
					state: ResponseState.Done,
					results: []
				} as GroupMemberOperationResponse
			)
		);
	}

	private initService(): void {
		if (this.isInitialized) {
			return;
		}

		this.isInitialized = true;
		this.destroy$ = new Subject();

		this.groupsLoadingComplete$.next(false);
		this.subscribeToGroupEvents();
		this.subscriptionReady$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
			this.queryAllGroups(null);
		});
	}

	private suspendService(): void {
		this.isInitialized = false;
		this.groupsLoadingComplete$.next(true);
		this.destroy$.next();
		this.destroy$.complete();
		this.groupSubscriptionManager.deregisterAll();

		this.suspended$.next();
	}

	private subscribeToGroupEvents(): void {
		this.groupsSubscriptionGQL
			.subscribe({ variables: {}, fetchPolicy: 'network-only' })
			.pipe(retry({ count: 2, delay: 10_000 }), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const event = result.data?.groups;
					if (!event) {
						return;
					}

					if ('added' in event) {
						const group = mapGroupFromSysApi(event.added);
						if (group) {
							this.groupAdded$.next(group);
							this.groupSubscriptionManager.register([group.id]);
							this.groupIds.add(group.id);
						}
					} else if ('removed' in event) {
						this.groupSubscriptionManager.deregister(event.removed);
						this.groupIds.delete(event.removed);
						this.groupRemoved$.next(event.removed);
					} else if ('ready' in event) {
						this.subscriptionReady$.next();
					}
				},
				error: (error: unknown) => {
					this.logger.error('subscribeToGroupEvents', 'groups subscription error', error);
				}
			});
	}

	private queryAllGroups(afterCursor: string | null): void {
		this.groupsQueryGQL
			.fetch({
				variables: { paginationArgs: { first: QUERY_PAGE_SIZE, after: afterCursor } },
				fetchPolicy: 'network-only'
			})
			.pipe(retry({ count: 2, delay: 5_000 }), take(1), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const connection = result.data?.groupsConnection;
					if (!connection) {
						return;
					}

					for (const edge of connection.edges) {
						if (edge.node) {
							const group = mapGroupFromSysApi(edge.node);
							if (group) {
								this.groupAdded$.next(group);
								this.groupSubscriptionManager.register([group.id]);
								this.groupIds.add(group.id);
							}
						}
					}

					if (connection.pageInfo.hasNextPage && connection.pageInfo.endCursor) {
						this.queryAllGroups(connection.pageInfo.endCursor);
					} else {
						this.groupsLoadingComplete$.next(true);
					}
				},
				error: (error: unknown) => {
					this.logger.error('queryAllGroups', 'failed to query groups', error);
					this.groupsLoadingComplete$.next(true);
				}
			});
	}

	private createGroupSubscription({
		id,
		retryCallback,
		eventObserver
	}: SubscriptionManagerConfigCreate): Subscription {
		return this.groupSubscriptionGQL
			.subscribe({
				variables: {
					id
				},
				errorPolicy: 'ignore',
				fetchPolicy: 'network-only'
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const groupData = result.data?.nodeChanges;

					if (groupData && '__typename' in groupData && groupData.__typename === 'Group') {
						const group = mapGroupFromSysApi(groupData as GroupFragment);
						if (group) {
							this.groupSnapshot$.next(group);
						}
					}

					if (eventObserver) {
						eventObserver.eventReceived();
					}
				},
				complete: () => {
					this.logger.error(
						'groupSubscriptionGQL',
						'subscription completed. Will retry.',
						JSON.stringify({ nodeId: id })
					);
					retryCallback();
				},
				error: (error: unknown) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'groupSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ id, error }, null, 3)
					);
					retryCallback();
				}
			});
	}
}
